import { Box, Typography } from "@mui/material";

import {
  educationEnum,
  genderEnum,
  RegistrationStatusEnum,
} from "enums/shared";
import { useOrderContext } from "contexts/OrderContext";
import ContentCard from "Components/shared/ContentCard";
import { useRTL } from "hooks/isRTL";
import LoadingBanner from "Components/shared/LoadingBanner";
import { fallbackValue } from "constants/fallbackConstant";

/**
 * Displays personal and professional details of the driver associated with the order.
 */
const DriverDetails = () => {
  const { driverDetails, isLoading, isError } = useOrderContext();
  const { t } = useRTL();

  if (isLoading) return <LoadingBanner />;
  if (isError) return <p>Error loading driver details.</p>;

  if (!driverDetails?.fullName) {
    return (
      <Typography
        variant="h2"
        sx={{
          textAlign: "center",
          margin: "1rem",
        }}
      >
        {t("noDriverAssignedYet")}
      </Typography>
    );
  }

  const getStatusLabel = (status) => {
    const entry = Object.entries(RegistrationStatusEnum).find(
      ([, value]) => value === status
    );
    return entry ? t(entry[0]) : fallbackValue;
  };

  const personalInfo = [
    { label: t("driverName"), value: driverDetails?.fullName || fallbackValue },
    {
      label: t("birthday"),
      value: driverDetails?.birthday
        ? driverDetails.birthday.split("-").reverse().join("/")
        : fallbackValue,
    },
    { label: t("city"), value: driverDetails?.city || fallbackValue },
    {
      label: t("education"),
      value: t(educationEnum[driverDetails?.education]) || fallbackValue,
    },
    {
      label: t("gender"),
      value: t(genderEnum[driverDetails?.gender]) || "Unknown",
    },
    {
      label: t("englishSpeaker"),
      value: driverDetails?.englishSpeaker ? t("yes") : t("no"),
    },
    {
      label: t("drivernationalNumber"),
      value: driverDetails?.nationalNumber || fallbackValue,
    },
    {
      label: t("driverPhoneNumber"),
      value: driverDetails?.phoneNumber.replace("_", "") || fallbackValue,
    },
  ];

  const professionalInfo = [
    {
      label: t("status"),
      value: getStatusLabel(driverDetails?.status),
    },
    { label: t("orderCount"), value: driverDetails?.orderCount || fallbackValue },
    { label: t("orderLimit"), value: driverDetails?.orderLimit || fallbackValue },
    {
      label: t("isOnline"),
      value: driverDetails?.isOnline ? t("yes") : t("no"),
    },
    {
      label: t("collectedOrderCash"),
      value: `${driverDetails?.collectedOrderCash || fallbackValue} JD`,
    },
    {
      label: t("toBeCollectedOrderCash"),
      value: `${driverDetails?.toBeCollectedOrderCash || fallbackValue} JD`,
    },
    {
      label: t("myEarnings"),
      value: `${driverDetails?.myEarnings || fallbackValue} JD`,
    },
    {
      label: t("expectedEarnings"),
      value: `${driverDetails?.expectedEarnings || fallbackValue} JD`,
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "1rem",
        padding: "1rem",
      }}
    >
      <ContentCard title={t("personalInfo")} content={personalInfo} />

      <ContentCard title={t("professionalInfo")} content={professionalInfo} />
    </Box>
  );
};

export default DriverDetails;
